import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import config from "../config";
// Create context
const ProfileContext = createContext(null);

export const ProfileProvider = ({ children }) => {
  const auth = useSelector((state) => state.auth || {});
  const token = localStorage.getItem("token");
  //const { customer_data } = auth || {};
  const { customer_data, isSecondaryUser, secondaryUserInfo } = auth || {}; //new

  const [customerDetails, setCustomerDetails] = useState(null);
  const [loader, setLoading] = useState(true); // <-- loading state
  const [displayDetails, setDisplayDetails] = useState(null); //new
  const clientId = customer_data?._id || customer_data?.id;

  // Fetch customer details
  const fetchCustomerDetails = useCallback(async () => {
    if (!clientId || !token) {   
      setLoading(false); // finish loading even if skipped
      return;
    } 
    setLoading(true);

    try {
      const res = await axios.get(
        `${config.API_BASE_URL}/customer/getcustomerdetails`,
        {
          params: { customer_id: clientId },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      console.log("[ProfileContext] Fetch response:", res.data);

      if (res.data?.success) {
        const primaryData = res.data.data;
        setCustomerDetails(res.data.data);

        // If secondary user, use the info from token/secondaryUsers array
        if (isSecondaryUser && secondaryUserInfo) {
          // Try to find the user in secondaryUsers array for complete details
          const myInfo = primaryData.secondaryUsers?.find(
            user => user.phone === secondaryUserInfo.phone
          );
          
          if (myInfo) {
            setDisplayDetails({
              name: myInfo.name,
              email: myInfo.email || "",
              phone: myInfo.phone,
              address: myInfo.address || null,
              isSecondaryUser: true,
              addedAt: myInfo.addedAt,
              verifiedAt: myInfo.verifiedAt
            });
          } else {
            // Fallback to token data
            setDisplayDetails({
              name: secondaryUserInfo.name || "Secondary User",
              phone: secondaryUserInfo.phone || "",
              email: secondaryUserInfo.email || "",
              isSecondaryUser: true
            });
          }
        } else {
          // Primary user
          setDisplayDetails({
            name: `${primaryData.firstname || ""} ${primaryData.lastname || ""}`.trim() || primaryData.name || "User",
            email: primaryData.email || "",
            phone: primaryData.contact || "",
            address: primaryData.address || null,
            isSecondaryUser: false,
            secondaryUsers: primaryData.secondaryUsers || []
          });
        }
      }
      
    //  } else {
      
     // }
    } catch (err) {
      console.error("[ProfileContext] Fetch customer error:", err);
    } finally {
      setLoading(false); // done loading
    }
  }, [clientId, token, isSecondaryUser, secondaryUserInfo]);

  useEffect(() => {
    fetchCustomerDetails();
  }, [fetchCustomerDetails]);

  const updateCustomer = async (updatedData) => {
    // Prevent secondary users from updating
    if (isSecondaryUser) {
      toast.error("Secondary users cannot update profile");
      return null;
    }

    const contactToUse = updatedData.contact || customerDetails?.contact;
    
    if (!token || !contactToUse) {
      
      return null;
    }

    console.log("[ProfileContext] Updating customer details with:", updatedData);

    try {
      const res = await axios.post(
        `${config.API_BASE_URL}/customer/updatecustomer`,
        { ...updatedData, contact: contactToUse },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log("[ProfileContext] Update response:", res.data);
      
      if (res.data?.success) {        
        
        // Immediately update state with the response data
        const updatedDetails = res.data.data;
        setCustomerDetails(updatedDetails);
        
        // Also trigger a background refresh to ensure we have the complete data
        // This uses a timeout to avoid race conditions
        setTimeout(() => {
          fetchCustomerDetails();
        }, 100);
        
        return updatedDetails;
      } else {
        console.log("[ProfileContext] Update failed:", res.data);
        return null;
      }
    } catch (err) {
      console.error("[ProfileContext] Update customer error:", err);
      return null;
    }
  };

  // Add refetchCustomer function - this simply calls fetchCustomerDetails
  const refetchCustomer = async () => {
    await fetchCustomerDetails();
  };

  return (
    <ProfileContext.Provider
      value={{
        customerDetails, // pass null if not loaded, let components handle it
        displayDetails,//new
        loader, 
        updateCustomer,
        fetchCustomerDetails,
        refetchCustomer,
        isSecondaryUser: isSecondaryUser || false, //new
        secondaryUserInfo, //new
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
};

// Hook to safely use profile context
export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error("useProfile must be used within a ProfileProvider");
  }
  return context;
};
